import React, { useState, useRef, useEffect } from 'react';
import { Box, TextField, Button, Avatar, Chip, IconButton, CircularProgress, Typography, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import CloseIcon from '@mui/icons-material/Close';
import PublicIcon from '@mui/icons-material/Public';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { createPost, editPost } from '../../services/api';
import GameSelectDialog from '../dialogs/GameSelectDialog';
import SubscriberBadge from '../common/SubscriberBadge';
import { useUser } from '../../contexts/UserContext';
import { isSubscribed, getPostMaxLength } from '../../utils/subscription';

interface GameTag {
  game_id: number;
  name: string;
  cover?: string | null;
}

interface PostComposerProps {
  avatar?: string | null;
  displayName?: string;
  onPostCreated?: () => void;
  autoFocus?: boolean;
  // 編集モード用
  mode?: 'create' | 'edit';
  postId?: number;
  initialContent?: string;
  initialGame?: GameTag | null;
  initialVisibility?: number;
  onPostUpdated?: () => void;
  onCancel?: () => void;
  embedded?: boolean; // PostCard 内に埋め込む際は avatar / 外側 padding を省略
}


const VISIBILITY_OPTIONS = [
  { value: 0, label: '通常', description: '', icon: <PublicIcon sx={{ fontSize: '1rem' }} /> },
  { value: 1, label: 'サイレント', description: 'フォロワーのTLに流れません', icon: <VolumeOffIcon sx={{ fontSize: '1rem' }} /> },
  { value: 2, label: '非公開', description: '自分だけが閲覧できます', icon: <VisibilityOffIcon sx={{ fontSize: '1rem' }} /> },
];

const PostComposer: React.FC<PostComposerProps> = ({
  avatar,
  displayName,
  onPostCreated,
  autoFocus,
  mode = 'create',
  postId,
  initialContent = '',
  initialGame = null,
  initialVisibility = 0,
  onPostUpdated,
  onCancel,
  embedded = false,
}) => {
  const { user } = useUser();
  const hasPaidPlan = isSubscribed(user?.subscription_plan ?? 0);
  const maxContentLength = getPostMaxLength(user?.subscription_plan ?? 0);
  const isEdit = mode === 'edit';
  const [content, setContent] = useState(initialContent);
  const [gameTag, setGameTag] = useState<GameTag | null>(initialGame);
  const [gameDialogOpen, setGameDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFocused, setIsFocused] = useState(!!autoFocus || isEdit);
  const textFieldRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (autoFocus || isEdit) {
      setTimeout(() => textFieldRef.current?.focus(), 100);
    }
  }, [autoFocus, isEdit]);
  const [visibility, setVisibility] = useState(initialVisibility);
  const [visibilityAnchor, setVisibilityAnchor] = useState<null | HTMLElement>(null);

  const handleSubmit = async () => {
    if (!content.trim() || isSubmitting) return;
    if (content.length > maxContentLength) return;

    setIsSubmitting(true);
    try {
      const payload = {
        content: content.trim(),
        game: gameTag?.game_id || null,
        visibility,
      };
      if (isEdit && postId != null) {
        await editPost(postId, payload);
        onPostUpdated?.();
      } else {
        await createPost(payload);
        setContent('');
        setGameTag(null);
        setVisibility(0);
        setIsFocused(false);
        onPostCreated?.();
      }
    } catch (error) {
      console.error(isEdit ? 'ポストの更新に失敗しました:' : '投稿に失敗しました:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSubmit();
    }
  };

  const remainingChars = maxContentLength - content.length;

  return (
    <>
      <Box
        sx={
          embedded
            ? { width: '100%' }
            : {
                p: { xs: 1, sm: 2 },
                borderBottom: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'rgba(255, 255, 255, 0.02)',
              }
        }
      >
        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 } }}>
          {!embedded && (
            <Avatar
              src={avatar || undefined}
              sx={{ width: { xs: 36, md: 44 }, height: { xs: 36, md: 44 }, bgcolor: 'grey.800', fontSize: '0.9rem' }}
            >
              {displayName?.[0]?.toUpperCase()}
            </Avatar>
          )}

          <Box sx={{ flex: 1 }}>
            <TextField
              multiline
              minRows={1}
              maxRows={8}
              placeholder="何を考えてる？"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onKeyDown={handleKeyDown}
              fullWidth
              variant="standard"
              inputRef={textFieldRef}
              slotProps={{
                input: {
                  disableUnderline: true,
                  sx: {
                    fontSize: '0.9rem',
                    lineHeight: 1.4,
                    '&::placeholder': { color: 'text.secondary' },
                  },
                },
              }}
            />

            {/* ゲームタグ表示 */}
            {gameTag && (
              <Chip
                icon={<SportsEsportsIcon sx={{ fontSize: '0.85rem !important' }} />}
                label={gameTag.name}
                size="small"
                onDelete={() => setGameTag(null)}
                deleteIcon={<CloseIcon sx={{ fontSize: '0.8rem !important' }} />}
                sx={{
                  mt: 1,
                  height: 26,
                  maxWidth: { xs: 200, sm: 'none' },
                  fontSize: '0.7rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.06)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              />
            )}

            {/* アクションバー */}
            {(isFocused || content || isEdit) && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5 }}>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Button
                    size="small"
                    startIcon={<SportsEsportsIcon sx={{ fontSize: '1rem !important' }} />}
                    onClick={() => setGameDialogOpen(true)}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      px: 1.5,
                      py: 0.5,
                      fontSize: { xs: '0.6rem', sm: '0.7rem' },
                      textTransform: 'none',
                      color: 'text.secondary',
                      minHeight: 0,
                      lineHeight: 1,
                    }}
                  >
                    ゲームを選択
                  </Button>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {visibility === 0 ? (
                    <IconButton
                      size="small"
                      onClick={(e) => setVisibilityAnchor(e.currentTarget)}
                      sx={{ p: 0.5, color: 'text.secondary' }}
                    >
                      {VISIBILITY_OPTIONS[0].icon}
                    </IconButton>
                  ) : (
                    <Button
                      size="small"
                      startIcon={VISIBILITY_OPTIONS[visibility].icon}
                      onClick={(e) => setVisibilityAnchor(e.currentTarget)}
                      sx={{
                        textTransform: 'none',
                        fontSize: '0.7rem',
                        color: 'primary.main',
                        minHeight: 0,
                        px: 1,
                        py: 0.25,
                        lineHeight: 1,
                      }}
                    >
                      {VISIBILITY_OPTIONS[visibility].label}
                    </Button>
                  )}
                  {content.length > 0 && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: remainingChars < 50 ? (remainingChars < 0 ? 'error.main' : 'warning.main') : 'text.secondary',
                        fontSize: '0.7rem',
                      }}
                    >
                      {remainingChars}
                    </Typography>
                  )}
                  {isEdit && (
                    <Button
                      size="small"
                      onClick={onCancel}
                      disabled={isSubmitting}
                      sx={{
                        borderRadius: 4,
                        textTransform: 'none',
                        fontSize: '0.8rem',
                        px: 1.5,
                        py: 0.5,
                        minHeight: 0,
                        color: 'text.secondary',
                      }}
                    >
                      キャンセル
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSubmit}
                    disabled={!content.trim() || isSubmitting || content.length > maxContentLength}
                    sx={{
                      borderRadius: 4,
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      px: 2.5,
                      py: 0.5,
                      minHeight: 0,
                    }}
                  >
                    {isSubmitting ? <CircularProgress size={16} /> : (isEdit ? '更新' : '投稿')}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <GameSelectDialog
        open={gameDialogOpen}
        onClose={() => setGameDialogOpen(false)}
        onSelect={(game) => {
          setGameTag({
            game_id: game.game_id,
            name: game.japanese_name || game.name,
            cover: game.cover,
          });
          setGameDialogOpen(false);
        }}
        title="ゲームをタグ付け"
      />

      <Menu
        anchorEl={visibilityAnchor}
        open={Boolean(visibilityAnchor)}
        onClose={() => setVisibilityAnchor(null)}
      >
        {VISIBILITY_OPTIONS.map((option) => {
          const locked = option.value === 2 && !hasPaidPlan;
          return (
            <MenuItem
              key={option.value}
              selected={visibility === option.value}
              disabled={locked}
              onClick={() => {
                setVisibility(option.value);
                setVisibilityAnchor(null);
              }}
              sx={{ py: 1, alignItems: 'flex-start' }}
            >
              <ListItemIcon sx={{ minWidth: 28, mt: 0.3 }}>{option.icon}</ListItemIcon>
              <ListItemText
                primary={option.label}
                secondary={option.description || undefined}
                primaryTypographyProps={{ fontSize: '0.8rem' }}
                secondaryTypographyProps={{ fontSize: '0.7rem', mt: 0.25 }}
              />
              {option.value === 2 && <SubscriberBadge variant="inline" sx={{ ml: 1, mt: 0.3 }} />}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default PostComposer;
